import { z } from 'zod';
import { ACCOUNT_KIND_VALUES } from './club.js';
import type { EntityVerificationStatus } from './verification.js';

/**
 * F11 b2b-accounts types + Zod schemas.
 *
 * Decisions: see plans/11_F11_b2b_account_venue_claim.md (Decisions 2, 16).
 *
 * ─── B2bAccountStatus lifecycle ──────────────────────────────────────────────
 *   active (default) → suspended (admin override) → disabled
 *
 * ─── Account model ──────────────────────────────────────────────────────────
 *
 * One `b2b_accounts` row → many venues via `venues.owner_b2b_account_id` FK.
 * The signing user is the primary owner; F13 will layer staff RBAC on top via
 * a future `b2b_account_members` table — F11 ships owner-only.
 *
 * Required fields at signup: legal_business_name (3-200 chars), display_name
 * (2-80 chars), country_code (ISO 3166-1 alpha-2, defaults to 'LB'). Optional:
 * tax_id (Lebanese VAT is optional). No logo/website/address at MVP.
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

export const B2B_ACCOUNT_STATUSES = ['active', 'suspended', 'disabled'] as const;
export type B2bAccountStatus = (typeof B2B_ACCOUNT_STATUSES)[number];

// ─── Raw DB row shapes ───────────────────────────────────────────────────────

export type B2bAccount = {
  readonly id: string;
  readonly legalBusinessName: string;
  readonly displayName: string;
  readonly countryCode: string;
  readonly taxId: string | null;
  readonly primaryOwnerUserId: string;
  readonly status: B2bAccountStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
  /**
   * F09 v2 (E3) — `b2b_accounts.verification_status`. Only `club` / `organizer`
   * accounts can be `verified` (DB CHECK).
   */
  readonly verificationStatus: EntityVerificationStatus;
  /** F09 v2 — display-only "since" date; null when unverified. */
  readonly verifiedAt: string | null;
  /** F09 v2 (E4) — display/legal names are locked while the account holds a tick. */
  readonly nameLocked: boolean;
};

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const B2bAccountStatusSchema = z.enum(B2B_ACCOUNT_STATUSES);

export const CreateB2bAccountInputSchema = z
  .object({
    legalBusinessName: z.string().min(3).max(200),
    displayName: z.string().min(2).max(80),
    countryCode: z.string().length(2).default('LB'),
    taxId: z.string().max(50).optional(),
  })
  .strict();
export type CreateB2bAccountInput = z.infer<typeof CreateB2bAccountInputSchema>;

/**
 * Update schema — `taxId` is additionally NULLABLE here (unlike create):
 * `taxId: null` explicitly CLEARS the stored tax id (the column is nullable),
 * while an absent key leaves it untouched. Identity audit fix
 * identity-f09-f11-002 — before this, no API path could clear a tax id and
 * the admin UI clear-attempt silently no-opped.
 */
export const UpdateB2bAccountInputSchema = CreateB2bAccountInputSchema.partial().extend({
  taxId: z.string().max(50).nullable().optional(),
});
export type UpdateB2bAccountInput = z.infer<typeof UpdateB2bAccountInputSchema>;

/**
 * Admin Super-Powers — POST /admin/b2b-accounts (super_admin, `b2b_accounts:create`).
 * Provision a B2B actor (venue/trainer/club/organizer) on a user's behalf:
 * the CreateB2bAccount fields + the owner user id + the account_kind
 * discriminator. The service appends the kind-correct role (venue_owner /
 * trainer / club_admin / tournament_organizer-on-approve) + companion profile
 * (trainer_profiles / organizer_profiles, auto-approved for organizer).
 */
export const AdminCreateActorInputSchema = CreateB2bAccountInputSchema.extend({
  primaryOwnerUserId: z.string().uuid(),
  accountKind: z.enum(ACCOUNT_KIND_VALUES),
}).strict();
export type AdminCreateActorInput = z.infer<typeof AdminCreateActorInputSchema>;
